'use client';

import { ReactNode, useCallback } from 'react';
import { CatBreadcrumbs } from '@blocks-react/components/Breadcrumbs';
import { CatBreadcrumbsItem } from '@blocks-react/components/BreadcrumbsItem';
import { CatTooltip } from 'components/blocks-components';
import { v4 as uuid } from 'uuid';
import { useTranslations } from 'next-intl';
import { BreadCrumb as BreadCrumbType } from './types';
import { isExternalReferrer } from '@cat-ecom/common/utils/browserUtils';
import styles from './bread-crumb-desktop.module.scss';
import cx from 'classnames';

export interface BreadCrumbClientProps {
  links: BreadCrumbType[];
  haveEllipsis?: boolean;
  ellipsisMaxCharLimit?: number;
  isHidingBackButton?: boolean;
  isWithoutActiveItem?: boolean;
  secondLastItemWithoutUnderline?: boolean;
}

const BreadCrumbDesktopClient = ({
  links = [],
  haveEllipsis = false,
  ellipsisMaxCharLimit = 20,
  isHidingBackButton = false,
  isWithoutActiveItem = false,
  secondLastItemWithoutUnderline = false
}: BreadCrumbClientProps) => {
  const t = useTranslations();
  const hideBackButton =
    typeof document !== 'undefined' &&
    (isExternalReferrer(document.referrer, document.location.host) ||
      isHidingBackButton);

  const getLabel = useCallback(
    (label: string | ReactNode) => {
      if (typeof label === 'string') {
        if (haveEllipsis && label.length > ellipsisMaxCharLimit) {
          return (
            <CatTooltip position="top">
              {label}
              <span slot="trigger">
                {`${label.substring(0, ellipsisMaxCharLimit)}...`}
              </span>
            </CatTooltip>
          );
        } else {
          return label;
        }
      }

      return label;
    },
    [ellipsisMaxCharLimit, haveEllipsis]
  );

  const lastLink = links.length - 1;
  const secondLastLink = lastLink - 1;
  const breadCrumbsItems = links.map((item, index) => {
    const displayingBreadcrumbLinkCondition = secondLastItemWithoutUnderline
      ? index === 0 || index < secondLastLink
      : index < lastLink;

    if (displayingBreadcrumbLinkCondition) {
      return (
        <CatBreadcrumbsItem key={uuid()} href={item.value}>
          <span className="text-capitalize">{getLabel(item.label)}</span>
        </CatBreadcrumbsItem>
      );
    } else {
      return (
        <CatBreadcrumbsItem
          key={uuid()}
          {...(!secondLastItemWithoutUnderline && { href: item.value })}
          className={cx({
            [styles['bread-crumb-desktop']]: secondLastItemWithoutUnderline
          })}
          isLast={index === lastLink}
          isActive={!isWithoutActiveItem}
        >
          <span className="text-capitalize">{getLabel(item.label)}</span>
        </CatBreadcrumbsItem>
      );
    }
  });

  return (
    <CatBreadcrumbs>
      <div data-testid="breadcrumbs" slot="breadcrumb">
        {!hideBackButton && (
          <CatBreadcrumbsItem key={uuid()} onClick={() => history.back()}>
            <span className="text-capitalize">{getLabel(t('UC_BACK'))}</span>
          </CatBreadcrumbsItem>
        )}
        {breadCrumbsItems}
      </div>
    </CatBreadcrumbs>
  );
};

export default BreadCrumbDesktopClient;
